import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Switch,
  LinearProgress,
  TextField,
} from "@mui/material";
import { C } from "../constants";
import { FEATURE_TOGGLES } from "./InputWizard";
import { FIELD_TONES, FancySelect, formatTime12 } from "./FormControls";
import { listScheduleRepo, getScheduleRepoEntry } from "../api";

function clip(text, n = 180) {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

const TONE_MAP = {
  ok: { accent: "#2E6BE6", soft: "#EAF0FF", chip: "#173673", label: "Ready" },
  warn: { accent: "#143B56", soft: "#E7F0F6", chip: "#143B56", label: "Caution" },
  bad: { accent: C.error, soft: C.errorBg, chip: "#B81422", label: "Blocked" },
  muted: { accent: "#64748B", soft: "#F1F5F9", chip: "#475569", label: "Off" },
};

const panelSx = {
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  bgcolor: C.surface,
};

function StatusMark({ tone }) {
  const t = TONE_MAP[tone] || TONE_MAP.muted;
  return (
    <Box
      aria-hidden
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: t.accent,
        flexShrink: 0,
      }}
    />
  );
}

function SectionLabel({ children, end }) {
  return (
    <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.25 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: C.muted,
        }}
      >
        {children}
      </Typography>
      {end}
    </Stack>
  );
}

/**
 * Preflight Review — checklist, modules, repository, generate.
 */
export default function PlanReviewPanel({
  memberCount = 1,
  numDays = 30,
  dailyStart = "09:00",
  dailyEnd = "18:00",
  lunchStart = "",
  lunchEnd = "",
  breaks = [],
  startDate = "",
  endDate = "",
  calendarReady = false,
  calendarOn = true,
  timingOn = true,
  teamOn = true,
  dtStageOn = true,
  activeDTStage = "",
  hasProblem = false,
  requiredIssues = [],
  problemVerified = false,
  problemText = "",
  teamProblemMap = [],
  enabledModules = {},
  onToggleModule,
  hasDsa = false,
  hasSystemDesign = false,
  hasSubjects = false,
  hasQuestions = false,
  generating = false,
  canGenerate = false,
  onGenerate,
  onJumpToProblem,
  repoMode = "generate_new",
  onRepoModeChange,
  repoMatch = null,
  repoEntryId = "",
  onRepoEntryChange,
  repoRegenParts = [],
  onRepoRegenPartsChange,
  repoAdaptNote = "",
  onRepoAdaptNoteChange,
  repoCampus = "",
  repoDepartment = "",
  repoYear = "",
  mustApplyText = "",
}) {
  const [showOptionalModules, setShowOptionalModules] = useState(false);
  const [manualSearch, setManualSearch] = useState("");
  const [manualPeers, setManualPeers] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);
  const [cohortEntries, setCohortEntries] = useState([]);
  const [cohortLoading, setCohortLoading] = useState(false);
  const [friendDeptFilter, setFriendDeptFilter] = useState("all"); // all | mine | <dept>
  const [friendYearFilter, setFriendYearFilter] = useState("all"); // all | mine | <year>
  const [friendDeptOptions, setFriendDeptOptions] = useState([]);
  const [friendYearOptions, setFriendYearOptions] = useState([]);
  const [customisePreset, setCustomisePreset] = useState("core"); // light | core | full | custom
  const [pickedDetail, setPickedDetail] = useState(null);

  const REPO_PARTS = [
    { id: "homework", label: "Homework", group: "Practice" },
    { id: "leetcode", label: "LeetCode", group: "Practice" },
    { id: "dsa", label: "DSA / Coding", group: "Practice" },
    { id: "system_design", label: "System Design", group: "Build" },
    { id: "project_build", label: "Project Build", group: "Build" },
    { id: "dt_playbook", label: "DT Playbook", group: "Build" },
    { id: "problem_review", label: "Problem Lab", group: "Learn" },
    { id: "learning", label: "Learning / Subjects", group: "Learn" },
    { id: "standup", label: "Stand-Up", group: "Day flow" },
    { id: "assessment", label: "Assessment / Speak & Solve", group: "Day flow" },
    { id: "peer_review", label: "Peer Review", group: "Day flow" },
    { id: "career", label: "Career / Placement", group: "Persona" },
    { id: "capability", label: "Capability practice", group: "Persona" },
    { id: "interest", label: "Interests / Goals", group: "Persona" },
  ];

  const CUSTOMISE_PRESETS = {
    light: [],
    core: ["homework", "leetcode", "dsa", "system_design", "learning", "project_build", "problem_review"],
    full: [
      "homework",
      "leetcode",
      "dsa",
      "system_design",
      "problem_review",
      "learning",
      "project_build",
      "dt_playbook",
      "standup",
      "assessment",
      "peer_review",
      "career",
      "capability",
      "interest",
    ],
  };

  const recommendation = repoMatch?.recommendation || repoMatch?.match || null;
  const profileLabel = repoMatch?.profileLabel || repoMatch?.cohortLabel || "";
  const softFingerprint = repoMatch?.softFingerprint || "";
  const cohortKey = repoMatch?.cohortKey || "";

  const formatScheduleMeta = (c) => {
    const bits = [];
    if (c?.department) bits.push(String(c.department).toUpperCase());
    if (c?.year) bits.push(`Year ${c.year}`);
    if (c?.semester) bits.push(`Sem ${c.semester}`);
    if (c?.numDays) bits.push(`${c.numDays} days`);
    if (c?.rowCount) bits.push(`${c.rowCount} rows`);
    return bits.join(" · ");
  };

  const displayMemberName = (c) => {
    const name = String(c?.memberName || "").trim();
    if (name) return name;
    const t = String(c?.title || "").trim();
    if (!t) return "Unnamed schedule";
    if (/days$/i.test(t) || /\s·\s/.test(t) || /year\s*\d/i.test(t)) return "Unnamed schedule";
    return t;
  };

  const tagLabel = (tag) => {
    const t = String(tag || "").toLowerCase();
    if (t === "best match" || t === "exact") return "Best match";
    if (t === "official" || t === "template") return "Official";
    if (t === "yours" || t === "personal") return "Yours";
    if (t === "friend" || t === "peer") return "Friend";
    if (t === "saved" || t === "cohort" || t === "similar") return "Saved";
    return tag || "Saved";
  };

  const campusForPick =
    repoCampus ||
    repoMatch?.key?.campus ||
    recommendation?.campus ||
    "";

  // Cohort schedules for Use saved / Customise
  useEffect(() => {
    if (repoMode !== "existing" && repoMode !== "existing_regen") {
      setCohortEntries([]);
      return undefined;
    }
    const fromLookup = Array.isArray(repoMatch?.cohortEntries)
      ? repoMatch.cohortEntries
      : Array.isArray(repoMatch?.candidates)
        ? repoMatch.candidates
        : [];
    if (fromLookup.length) {
      setCohortEntries(fromLookup);
      return undefined;
    }
    if (!softFingerprint && !cohortKey && !campusForPick) {
      setCohortEntries(recommendation ? [recommendation] : []);
      return undefined;
    }
    let cancelled = false;
    setCohortLoading(true);
    (async () => {
      try {
        const res = await listScheduleRepo({
          ...(softFingerprint ? { softFingerprint } : {}),
          ...(cohortKey ? { cohortKey } : {}),
          ...(!softFingerprint && campusForPick ? { campus: campusForPick } : {}),
          ...(repoDepartment ? { department: repoDepartment } : {}),
          ...(repoYear ? { year: repoYear } : {}),
          limit: 40,
        });
        if (!cancelled) {
          const rows = res?.entries || [];
          setCohortEntries(
            rows.length ? rows : recommendation ? [recommendation] : []
          );
        }
      } catch {
        if (!cancelled) {
          setCohortEntries(recommendation ? [recommendation] : []);
        }
      } finally {
        if (!cancelled) setCohortLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    repoMode,
    softFingerprint,
    cohortKey,
    campusForPick,
    repoDepartment,
    repoYear,
    recommendation,
    repoMatch?.cohortEntries,
    repoMatch?.candidates,
  ]);

  // Friend pick — campus-wide by default (optional dept / year filters)
  useEffect(() => {
    if (repoMode !== "manual") return undefined;
    if (!campusForPick) {
      setManualPeers([]);
      return undefined;
    }
    let cancelled = false;
    setManualLoading(true);
    const t = setTimeout(async () => {
      try {
        const deptParam =
          friendDeptFilter === "mine" && repoDepartment
            ? repoDepartment
            : friendDeptFilter !== "all" && friendDeptFilter !== "mine"
              ? friendDeptFilter
              : undefined;
        const yearParam =
          friendYearFilter === "mine" && repoYear
            ? repoYear
            : friendYearFilter !== "all" && friendYearFilter !== "mine"
              ? friendYearFilter
              : undefined;
        const res = await listScheduleRepo({
          campus: campusForPick,
          department: deptParam,
          year: yearParam,
          q: manualSearch.trim() || undefined,
          limit: 50,
        });
        if (!cancelled) {
          const rows = res?.entries || [];
          setManualPeers(rows);
          // Keep full dept/year option lists when browsing unfiltered campus
          if (friendDeptFilter === "all" && friendYearFilter === "all" && !manualSearch.trim()) {
            setFriendDeptOptions(
              Array.from(
                new Set(rows.map((c) => String(c.department || "").trim()).filter(Boolean))
              ).sort((a, b) => a.localeCompare(b))
            );
            setFriendYearOptions(
              Array.from(
                new Set(rows.map((c) => String(c.year || "").trim()).filter(Boolean))
              ).sort((a, b) => Number(a) - Number(b) || a.localeCompare(b))
            );
          }
        }
      } catch {
        if (!cancelled) setManualPeers([]);
      } finally {
        if (!cancelled) setManualLoading(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    repoMode,
    campusForPick,
    repoDepartment,
    repoYear,
    manualSearch,
    friendDeptFilter,
    friendYearFilter,
  ]);

  const scheduleOptionLabel = (c) => {
    const name = displayMemberName(c);
    const meta = formatScheduleMeta(c);
    const tag = c.matchTag ? ` · ${tagLabel(c.matchTag)}` : "";
    return meta ? `${name} — ${meta}${tag}` : `${name}${tag}`;
  };

  const cohortOptions = (cohortEntries.length
    ? cohortEntries
    : recommendation
      ? [recommendation]
      : []
  ).filter((c) => c?.id);

  const selectedSchedule =
    cohortOptions.find((c) => String(c.id) === String(repoEntryId)) ||
    manualPeers.find((c) => String(c.id) === String(repoEntryId)) ||
    (!repoEntryId && recommendation ? recommendation : null);

  useEffect(() => {
    const id = repoEntryId || (repoMode !== "manual" && repoMode !== "generate_new" ? recommendation?.id : "");
    if (!id || repoMode === "generate_new") {
      setPickedDetail(null);
      return undefined;
    }
    let cancelled = false;
    getScheduleRepoEntry(id)
      .then((res) => {
        if (!cancelled && res?.success) setPickedDetail(res.entry);
      })
      .catch(() => {
        if (!cancelled) setPickedDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [repoEntryId, recommendation?.id, repoMode]);

  const applyCustomisePreset = (preset) => {
    setCustomisePreset(preset);
    if (preset === "custom") return;
    onRepoRegenPartsChange?.(CUSTOMISE_PRESETS[preset] || []);
  };

  const togglePart = (id) => {
    if (!onRepoRegenPartsChange) return;
    setCustomisePreset("custom");
    const set = new Set(repoRegenParts || []);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onRepoRegenPartsChange([...set]);
  };

  const selectAllParts = () => {
    setCustomisePreset("full");
    onRepoRegenPartsChange?.(REPO_PARTS.map((p) => p.id));
  };
  const clearParts = () => {
    setCustomisePreset("light");
    onRepoRegenPartsChange?.([]);
  };

  const partGroups = ["Practice", "Build", "Learn", "Day flow", "Persona"];
  const shownPick = pickedDetail || selectedSchedule;

  const partsPicker = (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        ...panelSx,
        bgcolor: "#F8FAFC",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: C.muted, mb: 0.35 }}>
        Step 2 · What to rewrite
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 400, color: C.text, mb: 1.1, lineHeight: 1.5 }}>
        The saved timetable (days, times, activity names) stays. Only the content of selected parts is rewritten for your problem.
      </Typography>
      <FancySelect
        tone="teal"
        icon="ti-adjustments"
        label="Customise intensity"
        hint="Start with Core unless you need a light copy or a full rewrite"
        value={customisePreset}
        onChange={(e) => applyCustomisePreset(e.target.value)}
      >
        <option value="light">Light — keep almost everything, gentle wording adapt</option>
        <option value="core">Core — Homework, LeetCode, DSA, SD, Learning, Project, Problem Lab</option>
        <option value="full">Full — rewrite every activity type</option>
        <option value="custom">Custom — tap parts below</option>
      </FancySelect>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.25, mb: 0.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
          Parts to rewrite
        </Typography>
        <Stack direction="row" spacing={0.75}>
          <Button size="small" variant="text" onClick={selectAllParts} sx={{ minWidth: 0, fontSize: 12, fontWeight: 600, color: C.primary }}>
            All
          </Button>
          <Button size="small" variant="text" onClick={clearParts} sx={{ minWidth: 0, fontSize: 12, fontWeight: 600, color: C.muted }}>
            None
          </Button>
        </Stack>
      </Stack>
      <Typography sx={{ fontSize: 11.5, color: C.muted, fontWeight: 600, mb: 0.85 }}>
        {(repoRegenParts || []).length
          ? `${(repoRegenParts || []).length} part(s) selected — only those activities are rewritten.`
          : "No parts selected — AI lightly adapts the whole schedule to your problem."}
      </Typography>
      {partGroups.map((group) => {
        const items = REPO_PARTS.filter((p) => p.group === group);
        if (!items.length) return null;
        return (
          <Box key={group} sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: C.muted,
                mb: 0.5,
              }}
            >
              {group}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.65 }}>
              {items.map((p) => {
                const on = (repoRegenParts || []).includes(p.id);
                return (
                  <Box
                    key={p.id}
                    onClick={() => togglePart(p.id)}
                    sx={{
                      px: 1.1,
                      py: 0.5,
                      borderRadius: "6px",
                      cursor: "pointer",
                      border: `1px solid ${on ? C.primary : C.border}`,
                      bgcolor: on ? C.primarySoft : "#fff",
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: on ? C.primaryDark : C.text,
                    }}
                  >
                    {p.label}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
      <TextField
        size="small"
        fullWidth
        multiline
        minRows={2}
        placeholder="Step 3 · Optional: tell AI what to keep or change (e.g. keep Week 1, focus SD on auth)…"
        value={repoAdaptNote || ""}
        onChange={(e) => onRepoAdaptNoteChange?.(e.target.value)}
        sx={{
          mt: 0.5,
          "& .MuiOutlinedInput-root": {
            bgcolor: "#fff",
            fontSize: 12.5,
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );

  const selectedSummary = shownPick ? (
    <Box
      sx={{
        mt: 1,
        p: 1.25,
        ...panelSx,
        bgcolor: "#F8FAFC",
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.muted, mb: 0.35, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {repoMode === "existing" ? "Will load as-is" : "Seed schedule (then customise)"}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>
        {displayMemberName(shownPick)}
      </Typography>
      <Typography sx={{ fontSize: 12, color: C.muted, fontWeight: 600, mt: 0.2 }}>
        {formatScheduleMeta(shownPick) || "Dept / year not set"}
        {shownPick.homeworkRowCount ? ` · ${shownPick.homeworkRowCount} homework rows` : ""}
        {shownPick.weeklyRowCount ? ` · weekly included` : ""}
      </Typography>
      {(shownPick.problemPreview || shownPick.inputsSnapshot?.problem) ? (
        <Typography sx={{ fontSize: 11.5, color: C.faint || C.muted, fontWeight: 600, mt: 0.45, lineHeight: 1.4 }}>
          Problem: {clip(shownPick.problemPreview || shownPick.inputsSnapshot?.problem, 180)}
        </Typography>
      ) : (
        <Typography sx={{ fontSize: 11.5, color: C.muted, fontWeight: 600, mt: 0.35 }}>
          Problem preview will appear once this save includes a snapshot.
        </Typography>
      )}
    </Box>
  ) : null;

  const needsRepoPick =
    repoMode === "existing" ||
    repoMode === "existing_regen" ||
    repoMode === "manual";
  const hasRepoPick = Boolean(repoEntryId) || (repoMode !== "manual" && recommendation?.id);
  const generateBlockedByRepo =
    needsRepoPick &&
    ((repoMode === "manual" && !repoEntryId) ||
      ((repoMode === "existing" || repoMode === "existing_regen") &&
        !repoEntryId &&
        !recommendation));

  // Default customise intensity when entering customise / friend modes
  useEffect(() => {
    if (repoMode !== "existing_regen" && repoMode !== "manual") return;
    if ((repoRegenParts || []).length) return;
    setCustomisePreset("core");
    onRepoRegenPartsChange?.(CUSTOMISE_PRESETS.core);
  }, [repoMode]);

  const calendarDetail = !calendarOn
    ? { label: "Excluded", tone: "muted", detail: "Won’t use course dates" }
    : calendarReady
      ? { label: "Ready", tone: "ok", detail: startDate && endDate ? `${startDate} → ${endDate}` : "Calendar loaded" }
      : startDate && endDate
        ? { label: "Dates only", tone: "warn", detail: `${startDate} → ${endDate}` }
        : { label: "Missing", tone: "bad", detail: "Set start & end dates" };

  const mappedTeams = Array.isArray(teamProblemMap)
    ? teamProblemMap.filter((t) => t.members?.length)
    : [];
  const unverifiedTeamNames = mappedTeams
    .filter((t) => !t.verified)
    .map((t) => t.name || "Unassigned");
  const verifiedTeamNames = mappedTeams
    .filter((t) => t.verified)
    .map((t) => t.name || "Unassigned");

  const problemDetail = !hasProblem
    ? {
        label: "Required",
        tone: "bad",
        detail: mappedTeams.length
          ? `Add a problem for: ${unverifiedTeamNames.join(", ") || mappedTeams.map((t) => t.name).join(", ")}`
          : "Add a problem statement",
      }
    : mappedTeams.length >= 2
      ? (unverifiedTeamNames.length
        ? {
            label: "Unverified",
            tone: "warn",
            detail: `Still need verify: ${unverifiedTeamNames.join(", ")}`,
          }
        : {
            label: "Verified",
            tone: "ok",
            detail: `Verified: ${verifiedTeamNames.join(", ")}`,
          })
    : problemVerified
      ? { label: "Verified", tone: "ok", detail: "YC/Unicorn + thrust areas passed" }
      : { label: "Unverified", tone: "warn", detail: "Filled — verify recommended" };

  const checklist = [
    {
      key: "problem",
      icon: "ti-target",
      title: "Problem statement",
      ...problemDetail,
      action: hasProblem && !problemVerified && onJumpToProblem
        ? {
            label: unverifiedTeamNames[0] ? `Verify ${unverifiedTeamNames[0]}` : "Verify",
            onClick: onJumpToProblem,
          }
        : !hasProblem && onJumpToProblem
          ? { label: "Add", onClick: onJumpToProblem }
          : null,
    },
    {
      key: "team",
      icon: "ti-users",
      title: "Members",
      label: teamOn ? `${memberCount}` : "Off",
      tone: teamOn ? "ok" : "muted",
      detail: teamOn
        ? `${memberCount} member${memberCount === 1 ? "" : "s"} in this plan`
        : "Team section excluded",
    },
    {
      key: "timing",
      icon: "ti-clock",
      title: "Timing",
      label: timingOn ? `${numDays}d` : "Off",
      tone: timingOn ? "ok" : "muted",
      detail: timingOn
        ? `${numDays} days · ${formatTime12(dailyStart)}–${formatTime12(dailyEnd)}` +
          (lunchStart && lunchEnd
            ? ` · lunch ${formatTime12(lunchStart)}–${formatTime12(lunchEnd)}`
            : "") +
          ((breaks || []).filter((b) => b?.start && b?.end).length
            ? ` · ${(breaks || []).filter((b) => b?.start && b?.end).length} break(s)`
            : "")
        : "Daily window excluded",
    },
    {
      key: "calendar",
      icon: "ti-calendar",
      title: "Calendar",
      label: calendarDetail.label,
      tone: calendarDetail.tone,
      detail: calendarDetail.detail,
    },
    {
      key: "dt",
      icon: "ti-route",
      title: "DT stage",
      label: dtStageOn ? (activeDTStage || "Auto") : "Off",
      tone: dtStageOn ? "ok" : "muted",
      detail: dtStageOn
        ? `Start at ${String(activeDTStage || "auto").replace(/_/g, " ")}`
        : "Design Thinking stage excluded",
    },
    {
      key: "mustApply",
      icon: "ti-alert-circle",
      title: "Must apply",
      label: String(mustApplyText || "").trim() ? "On" : "Off",
      tone: String(mustApplyText || "").trim() ? "ok" : "muted",
      detail: String(mustApplyText || "").trim()
        ? clip(mustApplyText, 140)
        : "No extra rule — left empty",
    },
  ];

  const contentSignals = [
    { on: enabledModules.dsa !== false, filled: hasDsa, label: "DSA", tone: "coral" },
    { on: enabledModules.systemDesign !== false, filled: hasSystemDesign, label: "SysDesign", tone: "green" },
    { on: enabledModules.subjects !== false, filled: hasSubjects, label: "Subjects", tone: "teal" },
    { on: enabledModules.questions !== false, filled: hasQuestions, label: "Question banks", tone: "amber" },
    { on: enabledModules.assessment !== false, filled: true, label: "Assessment", tone: "blue" },
    { on: enabledModules.assessmentBanks !== false, filled: true, label: "Banks", tone: "slate" },
  ];

  const onCount = FEATURE_TOGGLES.filter((t) => enabledModules[t.key] !== false).length;
  const requiredMissing = (requiredIssues || []).filter((i) => !i.ok && i.id !== "problem");
  const blockers = [
    ...checklist.filter((c) => c.tone === "bad"),
    ...requiredMissing.map((i) => ({ key: `req_${i.id}`, tone: "bad", detail: i.detail })),
  ];
  const cautions = checklist.filter((c) => c.tone === "warn");
  const readyScore = checklist.filter((c) => c.tone === "ok").length;
  const readinessPct = Math.round((readyScore / checklist.length) * 100);
  const overallTone = blockers.length ? "bad" : cautions.length ? "warn" : "ok";
  const overall = TONE_MAP[overallTone];
  const overallLabel =
    blockers.length ? "Needs attention"
      : cautions.length ? "Almost ready"
        : "Ready to generate";
  const excerpt = clip(problemText, 220);

  const ESSENTIAL_KEYS = new Set(["timing", "team", "dtStage", "assessmentBanks"]);
  const essentialToggles = FEATURE_TOGGLES.filter((t) => ESSENTIAL_KEYS.has(t.key));
  const optionalToggles = FEATURE_TOGGLES.filter((t) => !ESSENTIAL_KEYS.has(t.key));
  const nextAction = !hasProblem
    ? "Add a problem statement — it is required."
    : blockers.length
      ? "Fix the items below, then generate."
      : "Looks good — generate your daily + weekly plan.";

  const renderModuleRow = (t) => {
    const on = enabledModules[t.key] !== false;
    return (
      <Box
        key={t.key}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.25,
          py: 0.85,
          mb: 0.65,
          borderRadius: "8px",
          border: `1px solid ${C.border}`,
          background: "#fff",
          opacity: on ? 1 : 0.72,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            bgcolor: "#F8FAFC",
            border: `1px solid ${C.border}`,
            color: on ? C.primary : C.faint,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className={`ti ${t.icon}`} style={{ fontSize: 15 }} />
        </Box>
        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 13.5,
            fontWeight: 500,
            color: on ? C.text : C.muted,
          }}
        >
          {t.label}
        </Typography>
        <Switch
          size="small"
          checked={on}
          onChange={(e) => onToggleModule?.(t.key, e.target.checked)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: C.primary },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: C.primary },
          }}
        />
      </Box>
    );
  };

  return (
    <Box>
      {/* Fast path */}
      <Box
        sx={{
          mb: 2,
          px: 2,
          py: 1.5,
          ...panelSx,
          bgcolor: "#F8FAFC",
          display: "flex",
          gap: 1.35,
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            bgcolor: "#fff",
            border: `1px solid ${C.border}`,
            color: C.primary,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className="ti ti-bolt" style={{ fontSize: 16 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>
            Fast path: problem → generate
          </Typography>
          <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.35, lineHeight: 1.5 }}>
            Required: problem statement, one member name, working days and daily start/end. Subjects, DSA, and banks are optional — leave them on if you filled them, or turn off what you don’t need.
          </Typography>
        </Box>
      </Box>

      {/* Readiness banner */}
      <Box
        sx={{
          mb: 2.75,
          px: { xs: 1.75, sm: 2.25 },
          py: 1.75,
          ...panelSx,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Stack direction="row" alignItems="center" spacing={1.35} sx={{ minWidth: 0 }}>
            <StatusMark tone={overallTone} />
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: C.text, letterSpacing: "-0.02em" }}>
                {overallLabel}
              </Typography>
              <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.2 }}>
                {nextAction}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ width: { xs: "100%", sm: 200 }, flexShrink: 0 }}>
            <LinearProgress
              variant="determinate"
              value={readinessPct}
              sx={{
                height: 8,
                borderRadius: 99,
                bgcolor: "rgba(10,14,23,0.08)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 99,
                  bgcolor: overall.accent,
                },
              }}
            />
            <Typography sx={{ mt: 0.6, fontSize: 12, color: C.muted, textAlign: "right", fontWeight: 500 }}>
              {readyScore}/{checklist.length} checks · {onCount} modules on
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1.05fr) minmax(300px, 1fr)" },
          gap: { xs: 2.5, md: 3 },
          alignItems: "start",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <SectionLabel>Preflight checklist</SectionLabel>
          <Stack spacing={1}>
            {checklist.map((row) => {
              const st = TONE_MAP[row.tone] || TONE_MAP.muted;
              return (
                <Box
                  key={row.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    px: 1.5,
                    py: 1.15,
                    borderRadius: "8px",
                    border: `1px solid ${C.border}`,
                    background: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "8px",
                      bgcolor: "#F8FAFC",
                      border: `1px solid ${C.border}`,
                      color: C.primary,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <i className={`ti ${row.icon}`} style={{ fontSize: 16 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                        {row.title}
                      </Typography>
                      <StatusMark tone={row.tone} />
                    </Stack>
                    <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.2 }} noWrap>
                      {row.detail}
                    </Typography>
                  </Box>
                  <Stack alignItems="flex-end" spacing={0.35} sx={{ flexShrink: 0, maxWidth: 140 }}>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.3,
                        borderRadius: "6px",
                        bgcolor: st.soft,
                        border: `1px solid ${st.accent}22`,
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: st.chip,
                      }}
                    >
                      {row.label}
                    </Box>
                    {row.action && (
                      <Button
                        size="small"
                        onClick={row.action.onClick}
                        sx={{
                          minWidth: 0,
                          px: 0.75,
                          py: 0,
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: "none",
                          color: C.primary,
                        }}
                      >
                        {row.action.label}
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          {mappedTeams.length >= 2 && (
            <Box sx={{ mt: 2.25 }}>
              <SectionLabel>Teams</SectionLabel>
              <Stack spacing={0.75}>
                {mappedTeams.map((t) => (
                  <Box
                    key={t.key || t.name}
                    sx={{
                      display: "flex",
                      gap: 1.25,
                      alignItems: "flex-start",
                      px: 1.5,
                      py: 1.1,
                      ...panelSx,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 650, color: C.text }}>
                        {t.name || "Unassigned"}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.25, lineHeight: 1.4 }}>
                        {String(t.problemStatement || "").trim()
                          ? clip(t.problemStatement, 140)
                          : "No statement yet"}
                      </Typography>
                    </Box>
                    <Typography sx={{
                      fontSize: 11.5,
                      fontWeight: 650,
                      color: t.verified ? "#047857" : C.muted,
                      flexShrink: 0,
                      pt: 0.2,
                    }}>
                      {t.verified ? "Verified" : "Open"}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {excerpt && mappedTeams.length < 2 && (
            <Box sx={{ mt: 2.25 }}>
              <SectionLabel>Problem preview</SectionLabel>
              <Box
                sx={{
                  px: 1.75,
                  py: 1.5,
                  ...panelSx,
                  bgcolor: "#F8FAFC",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    lineHeight: 1.55,
                    color: C.text,
                  }}
                >
                  {excerpt}
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 2.25 }}>
            <SectionLabel>Content attached</SectionLabel>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.85}>
              {contentSignals.map((s) => {
                const active = s.on && s.filled;
                const dim = !s.on;
                return (
                  <Box
                    key={s.label}
                    sx={{
                      px: 1.1,
                      py: 0.5,
                      borderRadius: "6px",
                      fontSize: 12.5,
                      fontWeight: 500,
                      border: `1px solid ${C.border}`,
                      bgcolor: active ? C.primarySoft : dim ? C.surfaceAlt : "#fff",
                      color: active ? C.primaryDark : dim ? C.faint : C.muted,
                      textDecoration: dim ? "line-through" : "none",
                    }}
                  >
                    {s.label}
                    {s.on && !s.filled ? " · empty" : ""}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <SectionLabel
            end={
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.muted, fontVariantNumeric: "tabular-nums" }}>
                {onCount}/{FEATURE_TOGGLES.length} included
              </Typography>
            }
          >
            Include in generation
          </SectionLabel>

          <Box
            sx={{
              p: 1.25,
              ...panelSx,
              bgcolor: "#F8FAFC",
            }}
          >
            {essentialToggles.map((t) => renderModuleRow(t))}
          </Box>

          <Button
            size="small"
            onClick={() => setShowOptionalModules((v) => !v)}
            sx={{
              mt: 1.25,
              mb: showOptionalModules ? 1 : 0,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              color: C.primary,
              px: 0.5,
            }}
            startIcon={<i className={`ti ${showOptionalModules ? "ti-chevron-up" : "ti-chevron-down"}`} />}
          >
            {showOptionalModules ? "Hide optional modules" : `Optional modules (${optionalToggles.length})`}
          </Button>

          {showOptionalModules && (
            <Box
              sx={{
                p: 1.25,
                ...panelSx,
                bgcolor: "#F8FAFC",
              }}
            >
              {optionalToggles.map((t) => renderModuleRow(t))}
            </Box>
          )}
          <Typography sx={{ mt: 1.1, fontSize: 11.5, color: C.faint, lineHeight: 1.4 }}>
            Off modules are ignored even if you filled them earlier.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2.75 }}>
        {/* Common schedule repository mode */}
        <Box
          sx={{
            mb: 1.75,
            p: 1.75,
            borderRadius: 2.5,
            border: `1.5px solid ${C.border}`,
            bgcolor: C.surface,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: C.muted, mb: 1 }}>
            Schedule repository
          </Typography>

          {/* Primary toggle: Generate new vs Use saved */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 0.75,
              mb: 1.25,
              p: 0.5,
              borderRadius: 2,
              bgcolor: C.surfaceAlt,
              border: `1px solid ${C.border}`,
            }}
          >
            {[
              {
                id: "generate_new",
                title: "Generate new",
                detail: "AI from your problem",
              },
              {
                id: "use_saved",
                title: "Use saved repository",
                detail: "Start from an old schedule",
              },
            ].map((opt) => {
              const savedOn =
                repoMode === "existing" ||
                repoMode === "existing_regen" ||
                repoMode === "manual";
              const selected =
                opt.id === "generate_new" ? repoMode === "generate_new" : savedOn;
              return (
                <Box
                  key={opt.id}
                  onClick={() => {
                    if (opt.id === "generate_new") onRepoModeChange?.("generate_new");
                    else if (!savedOn) onRepoModeChange?.("existing");
                  }}
                  sx={{
                    p: 1.1,
                    borderRadius: 1.75,
                    cursor: "pointer",
                    textAlign: "center",
                    border: `1px solid ${selected ? C.primary : "transparent"}`,
                    bgcolor: selected ? C.primarySoft : "transparent",
                  }}
                >
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: C.text }}>
                    {opt.title}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: C.muted, fontWeight: 600, mt: 0.2 }}>
                    {opt.detail}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {repoMode === "generate_new" ? (
            <Typography sx={{ fontSize: 12, color: C.muted, fontWeight: 600, mb: 0.5 }}>
              Creates a fresh plan. Nothing is shared until you click “Save my schedule” on Plans.
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                How do you want to use the saved schedule?
              </Typography>
              {[
                {
                  id: "existing",
                  title: "Load as-is",
                  detail: "Copy the saved schedule — no AI, no tokens.",
                },
                {
                  id: "existing_regen",
                  title: "Customise for my problem",
                  detail: "Start from saved, then AI rewrites selected parts for you.",
                },
                {
                  id: "manual",
                  title: "Pick a friend’s schedule",
                  detail: "Choose a classmate on your campus, then customise.",
                },
              ].map((opt) => {
                const selected = repoMode === opt.id;
                return (
                  <Box
                    key={opt.id}
                    onClick={() => onRepoModeChange?.(opt.id)}
                    sx={{
                      display: "flex",
                      gap: 1.25,
                      alignItems: "flex-start",
                      p: 1.15,
                      borderRadius: 2,
                      cursor: "pointer",
                      border: `1px solid ${selected ? C.primary : C.border}`,
                      bgcolor: selected ? C.primarySoft : "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        mt: 0.35,
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        border: `2px solid ${selected ? C.primary : C.borderStrong}`,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {selected ? (
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: C.primary }} />
                      ) : null}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                        {opt.title}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: C.muted, fontWeight: 600, mt: 0.2, lineHeight: 1.4 }}>
                        {opt.detail}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}

          {(repoMode === "existing" || repoMode === "existing_regen") && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text, mb: 0.75 }}>
                {repoMode === "existing" ? "Choose a saved schedule" : "Choose a schedule to customise"}
              </Typography>
              {profileLabel ? (
                <Typography sx={{ fontSize: 11.5, color: C.muted, fontWeight: 600, mb: 1 }}>
                  Matched to your profile · {profileLabel}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 11.5, color: C.muted, fontWeight: 600, mb: 1 }}>
                  Same campus, department, and year as your Team profile
                </Typography>
              )}

              {cohortLoading ? (
                <Typography sx={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>
                  Loading saved schedules…
                </Typography>
              ) : !cohortOptions.length ? (
                <Typography sx={{ fontSize: 12, color: FIELD_TONES.amber.chip, fontWeight: 700 }}>
                  No saved schedule for your campus, department, and year yet. Use Generate new, then click “Save my schedule” on Plans.
                </Typography>
              ) : (
                <>
                  <FancySelect
                    tone="navy"
                    icon="ti-database"
                    label="Saved schedule"
                    hint="Pick whose schedule to use — name, department, year, and days"
                    value={
                      repoEntryId ||
                      (recommendation?.id ? String(recommendation.id) : "")
                    }
                    onChange={(e) => onRepoEntryChange?.(String(e.target.value || ""))}
                  >
                    <option value="">Select a saved schedule…</option>
                    {cohortOptions.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {scheduleOptionLabel(c)}
                      </option>
                    ))}
                  </FancySelect>
                  {selectedSummary}
                </>
              )}

              {repoMode === "existing_regen" && (repoEntryId || recommendation) ? partsPicker : null}
            </Box>
          )}

          {repoMode === "manual" && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text, mb: 0.5 }}>
                Pick a friend’s schedule
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: C.muted, fontWeight: 600, mb: 1 }}>
                Choose a classmate who saved to the repository on your campus, then customise for your problem.
              </Typography>
              {!campusForPick ? (
                <Typography sx={{ fontSize: 12, color: FIELD_TONES.amber.chip, fontWeight: 700 }}>
                  Set your campus in Team profile first.
                </Typography>
              ) : (
                <Stack spacing={1.15}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1,
                    }}
                  >
                    <FancySelect
                      tone="teal"
                      icon="ti-building"
                      label="Department filter"
                      hint="Narrow classmates by department"
                      value={friendDeptFilter}
                      onChange={(e) => {
                        setFriendDeptFilter(e.target.value);
                        onRepoEntryChange?.("");
                      }}
                    >
                      <option value="all">All departments</option>
                      {repoDepartment ? (
                        <option value="mine">My department ({String(repoDepartment).toUpperCase()})</option>
                      ) : null}
                      {friendDeptOptions.map((d) => (
                        <option key={d} value={d}>
                          {String(d).toUpperCase()}
                        </option>
                      ))}
                    </FancySelect>
                    <FancySelect
                      tone="amber"
                      icon="ti-calendar"
                      label="Year filter"
                      hint="Narrow classmates by year / class"
                      value={friendYearFilter}
                      onChange={(e) => {
                        setFriendYearFilter(e.target.value);
                        onRepoEntryChange?.("");
                      }}
                    >
                      <option value="all">All years</option>
                      {repoYear ? (
                        <option value="mine">My year ({repoYear})</option>
                      ) : null}
                      {friendYearOptions.map((y) => (
                        <option key={y} value={y}>
                          Year {y}
                        </option>
                      ))}
                    </FancySelect>
                  </Box>

                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search by member name or problem…"
                    value={manualSearch}
                    onChange={(e) => setManualSearch(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: C.surfaceAlt,
                        fontSize: 13,
                        fontWeight: 600,
                      },
                    }}
                  />

                  {manualLoading ? (
                    <Typography sx={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>
                      Loading classmate schedules…
                    </Typography>
                  ) : !manualPeers.length ? (
                    <Typography sx={{ fontSize: 12, color: FIELD_TONES.amber.chip, fontWeight: 700 }}>
                      No friend schedules found yet. Ask them to generate and click “Save my schedule”.
                    </Typography>
                  ) : (
                    <>
                      <FancySelect
                        tone="coral"
                        icon="ti-users"
                        label="Classmate schedule"
                        hint={`${manualPeers.length} schedule(s) on your campus — pick one by name`}
                        value={repoEntryId || ""}
                        onChange={(e) => onRepoEntryChange?.(String(e.target.value || ""))}
                      >
                        <option value="">Select a classmate’s schedule…</option>
                        {manualPeers.map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {scheduleOptionLabel(c)}
                          </option>
                        ))}
                      </FancySelect>
                      {selectedSummary}
                    </>
                  )}
                </Stack>
              )}
              {repoEntryId ? partsPicker : null}
              {!repoEntryId && campusForPick && !manualLoading ? (
                <Typography sx={{ mt: 1, fontSize: 12, color: FIELD_TONES.amber.chip, fontWeight: 700 }}>
                  Select a classmate’s schedule from the dropdown before generating.
                </Typography>
              ) : null}
            </Box>
          )}

          {generateBlockedByRepo ? (
            <Typography sx={{ mt: 1, fontSize: 12, color: FIELD_TONES.amber.chip, fontWeight: 700 }}>
              {repoMode === "manual"
                ? "Pick a friend’s schedule first."
                : "Pick a saved schedule above, or switch to Generate new."}
            </Typography>
          ) : hasRepoPick && needsRepoPick ? (
            <Typography sx={{ mt: 1, fontSize: 11.5, color: C.success, fontWeight: 700 }}>
              Schedule selected — ready to generate.
            </Typography>
          ) : null}
        </Box>

        {(blockers.length > 0 || cautions.length > 0) && (
          <Box
            sx={{
              mb: 1.75,
              px: 1.75,
              py: 1.5,
              ...panelSx,
              bgcolor: blockers.length ? C.errorBg : C.primarySoft,
            }}
          >
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: C.text, mb: 0.6 }}>
              {blockers.length ? "Before you generate" : "Recommended before generate"}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
              {blockers.map((b) => (
                <Typography key={b.key} component="li" sx={{ fontSize: 12.5, color: C.inkSoft, mb: 0.3, fontWeight: 600 }}>
                  {b.detail}
                </Typography>
              ))}
              {cautions.map((b) => (
                <Typography key={b.key} component="li" sx={{ fontSize: 12.5, color: C.inkSoft, mb: 0.3, fontWeight: 600 }}>
                  {b.detail}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            p: { xs: 1.75, sm: 2.25 },
            ...panelSx,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            spacing={1.75}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.02em", color: C.text }}>
                {generating ? "Building your schedule…" : "Generate daily + weekly plan"}
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
                {!hasProblem
                  ? "Add a problem first — then one click builds the full schedule."
                  : "Uses your problem, timing, and included modules. Weekly follows daily."}
              </Typography>
            </Box>
            <Button
              variant="contained"
              disableElevation
              disabled={
                generating ||
                (!hasProblem && !onJumpToProblem) ||
                (hasProblem && !canGenerate) ||
                requiredMissing.length > 0 ||
                generateBlockedByRepo
              }
              onClick={() => {
                if (!hasProblem && onJumpToProblem) onJumpToProblem();
                else onGenerate?.();
              }}
              startIcon={
                <i className={`ti ${generating ? "ti-loader-2" : !hasProblem ? "ti-target" : "ti-player-play"}`} />
              }
              sx={{
                flexShrink: 0,
                px: 2.5,
                height: 44,
                fontSize: 14,
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                bgcolor: C.primary,
                color: "#fff",
                "&:hover": { bgcolor: C.primaryDark },
                minWidth: { sm: 168 },
              }}
            >
              {generating ? "Generating…" : !hasProblem ? "Go to problem" : "Generate plan"}
            </Button>
          </Stack>
          {generating && (
            <LinearProgress
              sx={{
                mt: 1.75,
                height: 3,
                borderRadius: 99,
                bgcolor: C.border,
                "& .MuiLinearProgress-bar": { bgcolor: C.primary },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
